import validateEmail from "@core/utils/validateEmail";
import type { MemberAggregate } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { diffUserAccess } from "@ext/enterprise/components/admin/settings/users/model/buildUserChanges";
import { Button } from "@ext/enterprise/components/admin/ui-kit/Button";
import { CancelButton } from "@ext/enterprise/components/admin/ui-kit/CancelButton";
import { CloseConfirmationDialog } from "@ext/enterprise/components/admin/ui-kit/CloseConfirmationDialog";
import { SheetComponent } from "@ext/enterprise/components/admin/ui-kit/SheetComponent";
import t from "@ext/localization/locale/translate";
import { Alert, AlertDescription, AlertIcon } from "@ui-kit/Alert";
import { LoadingButtonTemplate } from "@ui-kit/Button";
import { Field } from "@ui-kit/Field";
import { TextInput } from "@ui-kit/Input";
import { useMemo, useState } from "react";
import type { InviteMemberPayload, UserAccessChange } from "../members/types/GesCloudUsersComponentTypes";
import { getInviteErrorAlert, type InviteErrorAlert } from "./inviteMemberErrors";
import { MemberAccessDialogs, MemberAccessFields } from "./MemberAccessFields";
import { type MemberRoles, useMemberAccessForm } from "./useMemberAccessForm";

interface InviteMemberCardProps {
	open: boolean;
	onClose: () => void;
	aggregate: MemberAggregate;
	canManageAdmins: boolean;
	existingUsers: string[];
	showAccesses: boolean;
	onInvite: (payload: InviteMemberPayload) => Promise<void>;
}

const getEmailError = (email: string, existingUsers: string[]): string | undefined => {
	if (!email) return t("enterprise.admin.errors.email-required");
	if (!validateEmail(email)) return t("enterprise-guest.validationErrors.emailInvalidFormat");
	if (existingUsers.includes(email)) return t("enterprise.admin.users.errors.exists");
	return undefined;
};

export const InviteMemberCard = ({
	open,
	onClose,
	aggregate,
	canManageAdmins,
	existingUsers,
	showAccesses,
	onInvite,
}: InviteMemberCardProps) => {
	const [email, setEmail] = useState("");
	const [emailTouched, setEmailTouched] = useState(false);
	const [isInvited, setIsInvited] = useState(false);
	const [saving, setSaving] = useState(false);
	const [submitError, setSubmitError] = useState<InviteErrorAlert | null>(null);
	const [showUnsaved, setShowUnsaved] = useState(false);

	const trimmedEmail = email.trim();
	const emailValidationError = getEmailError(trimmedEmail, existingUsers);

	const canAddEditor = aggregate.editors.length < aggregate.editorsCount;
	const initialRoles = useMemo<MemberRoles>(
		() => ({ isEditor: canAddEditor, isAdmin: false, aiAgentAccess: false }),
		[canAddEditor],
	);

	const memberForm = useMemberAccessForm({ email: trimmedEmail, aggregate, initialRoles, isOwner: false });
	const { isEditor, isAdmin, aiAgentAccess } = memberForm.data;
	const { rows: accessRows, validate: validateAccesses } = memberForm.access;

	const hasChanges =
		Boolean(trimmedEmail) ||
		isEditor !== initialRoles.isEditor ||
		isAdmin ||
		aiAgentAccess ||
		accessRows.length > 0;

	const requestClose = () => {
		if (saving) return;
		if (hasChanges) setShowUnsaved(true);
		else onClose();
	};

	const submit = async () => {
		if (saving) return;
		setEmailTouched(true);
		if (emailValidationError || !(await validateAccesses())) return;

		setSaving(true);
		setSubmitError(null);
		try {
			await onInvite({
				email: trimmedEmail,
				workspaceRole: isEditor ? "editor" : "reader",
				membershipRole: isAdmin ? "admin" : "member",
				aiAgentAccess,
				accessChanges: diffUserAccess(trimmedEmail, [], accessRows) as UserAccessChange[],
			});
			setIsInvited(true);
			onClose();
		} catch (error) {
			setSubmitError(getInviteErrorAlert(error));
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<SheetComponent
				cancelButton={
					<CancelButton
						disabled={saving}
						onClick={(e) => {
							e.preventDefault();
							requestClose();
						}}
					/>
				}
				confirmButton={
					saving ? (
						<LoadingButtonTemplate text={t("enterprise-cloud.org-settings.users.invite")} />
					) : (
						<Button
							onClick={(e) => {
								e.preventDefault();
								void submit();
							}}
						>
							{t("enterprise-cloud.org-settings.users.invite")}
						</Button>
					)
				}
				isOpen={open}
				onOpenChange={(next) => !next && requestClose()}
				sheetContent={
					<div className="flex flex-col gap-6">
						{submitError && (
							<Alert focus="medium" status={submitError.status}>
								<AlertIcon icon={submitError.icon} />
								<AlertDescription>{submitError.message}</AlertDescription>
							</Alert>
						)}
						<MemberAccessFields
							canManageAdmins={canManageAdmins}
							emailField={
								<Field
									control={(fieldProps) => (
										<TextInput
											onBlur={() => setEmailTouched(true)}
											onChange={(value) => setEmail(value ?? "")}
											placeholder={t("enterprise-cloud.org-settings.users.email-placeholder")}
											type="email"
											value={email}
											{...fieldProps}
										/>
									)}
									error={emailTouched && !isInvited ? emailValidationError : undefined}
									layout="vertical"
									required
									title={t("email")}
								/>
							}
							form={memberForm}
							showAccesses={showAccesses}
						/>
					</div>
				}
				title={t("enterprise-cloud.org-settings.users.invite-form.title")}
			/>

			{showAccesses && <MemberAccessDialogs aggregate={aggregate} form={memberForm} />}

			<CloseConfirmationDialog isOpen={showUnsaved} onClose={onClose} onOpenChange={setShowUnsaved} />
		</>
	);
};
